import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
import { TableService } from './table.service';
import { TerminalDeLaSesion } from '../core/offline/terminal-de-la-sesion';
import { TerminalIdentity } from '../core/offline/terminal-identity';
import { db } from '../core/offline/db';
import { negocioDeLaSesionAbierta } from '../core/tenant/negocio-del-token';
import { leerError } from '../core/ui/motivo-del-error';
import { PerfilService } from '../core/perfil/perfil.service';

/**
 * Multisede, fase 1 (-mt 150203b): emparejar esta caja con su sede, una vez.
 *
 * <p>Solo pregunta si el negocio tiene 2+ sedes, la caja no está emparejada en el servidor y no hay código
 * guardado aquí. Con una sede no aparece nunca. «Ahora no» deja vender: lo vendido cae en la principal hasta
 * emparejar (ECM, 23/09: una caja sin emparejar funciona como hoy). Sin red, el código se guarda y la primera
 * venta que sincronice deja la caja en su sede. Textos de ECM; el 409 y el 404 son del servidor, tal cual.
 */
@Injectable({ providedIn: 'root' })
export class EmparejarCajaService {
    private readonly http = inject(HttpClient);
    private readonly mesas = inject(TableService);
    private readonly terminal = inject(TerminalDeLaSesion);
    private readonly perfil = inject(PerfilService);

    /** ¿Hay que preguntar? Lo decide lo que dijo `/account/sites/mode` y lo guardado aquí. */
    async hayQuePreguntar(): Promise<boolean> {
        const modo = this.mesas.modo();
        // Sin `cajaEmparejada` también se pregunta: una caja recién instalada aún no tiene UUID, el modo se pidió sin
        // terminalId y el servidor no puede decirlo. Un backend viejo no manda `sedes`: no se pregunta.
        if (!modo || (modo.sedes ?? 1) < 2 || modo.cajaEmparejada === true) return false;
        return !(await this.terminal.codigo());
    }

    /**
     * Pregunta si hace falta. Devuelve true si la caja quedó emparejada ahora (el modo de su sede puede cambiar).
     * Nunca lanza: emparejar es una ayuda, y un fallo aquí no puede dejar la caja sin cargar mesas ni vender.
     */
    async siHaceFalta(): Promise<boolean> {
        try {
            return await this.preguntar();
        } catch {
            return false;
        }
    }

    private async preguntar(): Promise<boolean> {
        if (!(await this.hayQuePreguntar())) return false;
        return this.pedirCodigo((sede) => `Listo: esta caja quedó en ${sede}.`);
    }

    /** La pantalla del código (la misma del primer emparejamiento y del «Emparejar de nuevo» de M26). */
    private async pedirCodigo(listo: (sede: string | undefined) => string): Promise<boolean> {
        const { isConfirmed, value } = await Swal.fire({
            // Barrido de textos (ECM): el término del perfil, sin artículo pegado. «Código de la sede» se queda literal.
            title: `¿En qué ${this.perfil.termino('sede')} está esta caja?`,
            text: 'Escribe el código que te dio SureSell',
            input: 'text',
            inputPlaceholder: 'Código de la sede',
            showCancelButton: true,
            confirmButtonText: 'Guardar',
            cancelButtonText: 'Ahora no',
            allowOutsideClick: false,
            showLoaderOnConfirm: true,
            preConfirm: async (codigo: string) => {
                if (!codigo?.trim()) return false;
                try {
                    return await this.emparejar(codigo);
                } catch (e) {
                    // leerError prefiere `mensaje`/`message`: en el 403 de otra sede `error` lleva el código, no el texto.
                    Swal.showValidationMessage(leerError(e, 'No se pudo guardar').texto);
                    return false;
                }
            },
        });
        if (!isConfirmed || !value) return false;
        void Swal.fire({ toast: true, position: 'bottom-start', showConfirmButton: false, timer: 3000, icon: 'success',
            title: value.sinRed ? `Sin conexión: guardamos el código, y la caja queda en su ${this.perfil.termino('sede')} con la primera venta que se envíe.`
                : listo(value.sede) });
        return !value.sinRed;
    }

    // ===== M26 (2) · ¿Esta caja sigue donde debe? (-mt c214643; textos de Producto §M26 2) =====

    /** Mínimo entre dos lecturas: el disparo tras cada pase de la cola puede llegar cada 7 s. */
    static readonly CADA_MS = 5 * 60_000;
    private ultimaRevision = Number.NEGATIVE_INFINITY;
    private revisando = false;
    /** «Ahora no» o «Entendido»: no se repite hasta el siguiente arranque (Producto: no se silencia para siempre). */
    private calladoHastaArrancar = false;

    /**
     * Lee el estado del emparejamiento si la caja tiene código. 404 (la caja aún no vendió: el servidor no la conoce)
     * o EMPAREJADA = nada que avisar. Nunca lanza: es una ayuda, no puede frenar la venta.
     */
    async revisarEmparejamiento(ahora: number = Date.now()): Promise<void> {
        if (this.calladoHastaArrancar || this.revisando || ahora - this.ultimaRevision < EmparejarCajaService.CADA_MS) return;
        this.revisando = true;
        try {
            const codigo = await this.terminal.codigo();
            const terminalId = await this.terminal.id();
            if (!codigo || !terminalId) return;
            this.ultimaRevision = ahora;
            let r: EstadoDelEmparejamiento;
            try {
                r = await firstValueFrom(this.http.get<EstadoDelEmparejamiento>(
                    `${environment.apiBaseUrl}/account/terminales/${encodeURIComponent(terminalId)}/emparejamiento`, { params: { codigo } }));
            } catch {
                return; // 404 o sin red: se vuelve a preguntar en el siguiente disparo
            }
            if (!r || r.estado === 'EMPAREJADA') return;
            await this.avisar(r);
        } catch {
            /* una ayuda nunca frena la venta */
        } finally {
            this.revisando = false;
        }
    }

    private async avisar(r: EstadoDelEmparejamiento): Promise<void> {
        const aviso = avisoDelEmparejamiento(r);
        if (!aviso) return;
        const { isConfirmed } = await Swal.fire({
            icon: 'warning',
            title: aviso.titulo,
            html: `<p>${esc(aviso.texto)}</p><p style="font-size:.85rem;color:#64748b">${esc(LINEA_FIJA)}</p>`,
            showCancelButton: aviso.puedeEmparejar,
            confirmButtonText: aviso.puedeEmparejar ? 'Emparejar de nuevo' : 'Entendido',
            cancelButtonText: 'Ahora no',
        });
        if (!aviso.puedeEmparejar || !isConfirmed) {
            this.calladoHastaArrancar = true;
            return;
        }
        await this.pedirCodigo((sede) => `Caja emparejada con ${sede ?? ''}. Las ventas nuevas entran ahí.`);
    }

    /**
     * POST emparejar. Sin red se guarda el código y se sigue. Un 409 o un 404 se lanzan: los pinta el modal.
     * El UUID se asegura con {@link TerminalIdentity.inicializar}, el mismo camino del arranque (no se toca cómo
     * se genera): una caja recién instalada todavía no lo tiene hasta su primera venta.
     */
    async emparejar(codigo: string): Promise<{ sede?: string; sinRed?: boolean }> {
        const negocio = negocioDeLaSesionAbierta();
        const terminalId = negocio ? await new TerminalIdentity(db).inicializar(negocio) : null;
        try {
            const res = await firstValueFrom(this.http.post<{ sede: string }>(
                `${environment.apiBaseUrl}/account/terminales/emparejar`, { codigo: codigo.trim(), terminalId }));
            await this.terminal.guardarCodigo(codigo);
            return { sede: res?.sede };
        } catch (e) {
            if (e instanceof HttpErrorResponse && e.status === 0) {
                await this.terminal.guardarCodigo(codigo);
                return { sinRed: true };
            }
            throw e;
        }
    }
}

/** `GET /account/terminales/{id}/emparejamiento` (-mt c214643). */
export interface EstadoDelEmparejamiento {
    estado: 'EMPAREJADA' | 'CODIGO_DESCONOCIDO' | 'EN_OTRA_SEDE' | 'SIN_SEDE';
    sede: { id: number; nombre: string } | null;
    sedeDelCodigo: string | null;
    codigo: string | null;
    sedePrincipal: string | null;
}

const LINEA_FIJA = 'Las ventas que ya subieron no se mueven solas: un administrador las corrige en el panel, en Negocio → Cajas.';

const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * M26 (2): el aviso por estado (textos de Producto). Las sedes con su nombre propio, no con el término. EN_OTRA_SEDE no
 * se arregla emparejando de nuevo (solo un administrador en «Cajas»): un solo botón. EMPAREJADA: sin aviso.
 */
export function avisoDelEmparejamiento(r: EstadoDelEmparejamiento): { titulo: string; texto: string; puedeEmparejar: boolean } | null {
    const principal = r.sedePrincipal ?? '';
    switch (r.estado) {
        case 'CODIGO_DESCONOCIDO':
            return { titulo: 'Esta caja no está emparejada', puedeEmparejar: true,
                texto: `El código ${r.codigo ?? ''} no existe, así que las ventas de esta caja están entrando en ${principal}, la principal.` };
        case 'SIN_SEDE':
            return { titulo: 'Esta caja no está emparejada', puedeEmparejar: true,
                texto: `Las ventas de esta caja están entrando en ${principal}, la principal.` };
        case 'EN_OTRA_SEDE': {
            const sede = r.sede?.nombre ?? '';
            const delCodigo = r.sedeDelCodigo ?? '';
            return { titulo: 'Esta caja está en otro lugar', puedeEmparejar: false,
                texto: `Esta caja está registrada en ${sede}, pero su código es de ${delCodigo}. Sus ventas entran en ${sede}. Si la caja está en ${delCodigo}, pídele a un administrador que la cambie en el panel, en Negocio → Cajas.` };
        }
        default:
            return null;
    }
}
